import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../lib/api';
import { useDropzone } from 'react-dropzone';
import DOMPurify from 'dompurify';
import {
  X,
  Edit2,
  Trash2,
  Save,
  Calendar,
  Timer,
  User,
  MessageSquare,
  Paperclip,
  Download,
  AlertCircle,
  Upload,
  Search,
  History,
  ArrowRight,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import MentionTextarea from './MentionTextarea';


function TicketDetailModal({ ticket, boards, customFields = [], onClose, onTicketUpdated, onTicketDeleted }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: ticket.title,
    description: ticket.description || '',
    priority: ticket.priority,
    column_id: ticket.column_id,
    assignee_user_id: ticket.assignee_user_id || 'unassigned',
    due_date: ticket.due_date ? ticket.due_date.slice(0, 10) : '',
    custom_fields: ticket.custom_fields || {},
  });
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [descriptionImages, setDescriptionImages] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('comments');
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!assigneeSearch.trim()) return users;
    const q = assigneeSearch.toLowerCase();
    return users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, assigneeSearch]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const response = await api.get(`/auth/me`);
      setCurrentUserId(response.data.user_id);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    const fetchSafe = async (promise) => {
      try { return await promise; } catch (e) { console.error('Partial load failed:', e); return null; }
    };

    const [columnsRes, usersRes, commentsRes, attachmentsRes, historyRes] = await Promise.all([
      fetchSafe(api.get(`/boards/${ticket.board_id}/columns`)),
      fetchSafe(api.get(`/employees/active`)),
      fetchSafe(api.get(`/tickets/${ticket.ticket_id}/comments`)),
      fetchSafe(api.get(`/tickets/${ticket.ticket_id}/attachments`)),
      fetchSafe(api.get(`/tickets/${ticket.ticket_id}/history`)),
    ]);
    if (columnsRes) setColumns(columnsRes.data);
    if (usersRes) setUsers(usersRes.data);
    if (commentsRes) setComments(commentsRes.data);
    if (attachmentsRes) setAttachments(attachmentsRes.data);
    if (historyRes) setHistory(historyRes.data);
  }, [ticket.board_id, ticket.ticket_id]);

  const loadDescriptionImages = useCallback(async () => {
    if (!ticket.description_images || ticket.description_images.length === 0) return;
    
    try {
      const response = await api.get(`/tickets/${ticket.ticket_id}/attachments`);
      const imageAttachments = response.data.filter((att) =>
        ticket.description_images.includes(att.attachment_id)
      );
      setDescriptionImages(imageAttachments);
    } catch (error) {
      console.error('Failed to load description images:', error);
    }
  }, [ticket.ticket_id, ticket.description_images]);

  useEffect(() => {
    loadData();
    loadDescriptionImages();
    loadCurrentUser();
  }, [loadData, loadDescriptionImages, loadCurrentUser]);

  // Check if current user is the ticket creator
  const isCreator = currentUserId && ticket.created_by === currentUserId;


  const handleUpdate = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        assignee_user_id: formData.assignee_user_id === 'unassigned' ? '' : formData.assignee_user_id
      };
      const response = await api.patch(
        `/tickets/${ticket.ticket_id}`,
        submitData,
        
      );
      toast.success('Ticket updated successfully');
      onTicketUpdated(response.data);
    } catch (error) {
      console.error('Failed to update ticket:', error);
      toast.error('Failed to update ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (fieldId, value, checked) => {
    setFormData(prev => {
      const currentValues = prev.custom_fields[fieldId] || [];
      let newValues;
      if (checked) {
        newValues = [...currentValues, value];
      } else {
        newValues = currentValues.filter(v => v !== value);
      }
      return {
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [fieldId]: newValues
        }
      };
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;

    setLoading(true);
    try {
      await api.delete(`/tickets/${ticket.ticket_id}`);
      toast.success('Ticket deleted successfully');
      onTicketDeleted(ticket.ticket_id);
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      toast.error('Failed to delete ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await api.post(
        `/comments`,
        { 
          ticket_id: ticket.ticket_id, 
          content: newComment.trim(),
          mentions: commentMentions
        },
        
      );
      setComments([...comments, response.data]);
      setNewComment('');
      setCommentMentions([]);
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await api.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter(a => a.attachment_id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  // Convert URLs in text to clickable links (with HTML escaping for safety)
  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const linkifyText = (text) => {
    if (!text) return '';
    // First escape HTML to prevent XSS
    const escaped = escapeHtml(text);
    // Then convert URLs to clickable links
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    return escaped.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>');
  };

  const renderCommentWithMentions = (content) => {
    if (!content) return '';
    // First escape HTML
    let processed = escapeHtml(content);
    // Then replace @[Name](user_id) with highlighted mentions
    processed = processed.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, (match, name, userId) => {
      return `<span class="inline-flex items-center px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-semibold text-sm">@${escapeHtml(name)}</span>`;
    });
    // Then convert URLs to clickable links
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    processed = processed.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>');
    return processed;
  };

  const formatTimeToComplete = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const onDrop = async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      // Check file size (50MB limit for videos, 10MB for others)
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const sizeLabel = isVideo ? '50MB' : '10MB';
      
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size is ${sizeLabel}`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('ticket_id', ticket.ticket_id);

      try {
        await api.post(`/attachments`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Failed to upload file:', error);
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    loadData(); // Reload to get updated attachments
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'video/*': ['.mp4', '.webm', '.mov']
    },
    multiple: true,
  });

  const downloadAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.file_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full border border-stone-200 flex flex-col max-h-[90vh]" data-testid="ticket-detail-modal">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-stone-900">Ticket Details</h2>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button
                  onClick={() => {
                    handleUpdate();
                    setEditMode(false);
                  }}
                  disabled={loading}
                  data-testid="save-ticket-button"
                  className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold"
                >
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setFormData({
                      title: ticket.title,
                      description: ticket.description || '',
                      priority: ticket.priority,
                      column_id: ticket.column_id,
                      assignee_user_id: ticket.assignee_user_id || 'unassigned',
                      due_date: ticket.due_date
                        ? ticket.due_date.slice(0, 10)
                        : '',
                      custom_fields: ticket.custom_fields || {},
                    });
                  }}
                  variant="outline"
                  data-testid="cancel-edit-button"
                  className="rounded-xl"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setEditMode(true)}
                  variant="outline"
                  data-testid="edit-ticket-button"
                  className="bg-white border border-stone-200 hover:bg-stone-50 hover:border-primary/30 text-stone-700 rounded-xl"
                >
                  <Edit2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Edit
                </Button>
                {isCreator && (
                  <Button
                    onClick={handleDelete}
                    variant="outline"
                    disabled={loading}
                    data-testid="delete-ticket-button"
                    className="bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-xl"
                  >
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Delete
                  </Button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              data-testid="close-ticket-detail-modal"
              className="p-2 hover:bg-stone-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-y-auto flex-1">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Title
              </label>
              {editMode ? (
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="edit-ticket-title"
                />
              ) : (
                <h3 className="text-2xl font-semibold text-stone-900">{ticket.title}</h3>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Description
              </label>
              {editMode ? (
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  onKeyDown={(e) => {
                    // Allow Enter to add new lines, don't submit form
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  rows={4}
                  data-testid="edit-ticket-description"
                />
              ) : (
                <>
                  <div 
                    className="text-sm text-stone-600 leading-relaxed mb-3 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(linkifyText(ticket.description || 'No description'), { ADD_ATTR: ['target', 'rel'] })
                    }}
                  />
                  {/* Display description images */}
                  {descriptionImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {descriptionImages.map((img) => (
                        <div
                          key={img.attachment_id}
                          className="relative group border border-stone-200 rounded-lg overflow-hidden"
                        >
                          <img
                            src={`data:${img.file_type};base64,${img.file_data}`}
                            alt={img.file_name}
                            className="w-32 h-32 object-cover"
                          />
                          <button
                            onClick={() => downloadAttachment(img)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <Download className="w-6 h-6 text-white" strokeWidth={1.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Tabs: Comments / Task History */}
            <div>
              <div className="flex gap-1 mb-4 border-b border-stone-200">
                <button
                  onClick={() => setActiveTab('comments')}
                  data-testid="tab-comments"
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'comments'
                      ? 'border-primary text-stone-900'
                      : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" strokeWidth={2} />
                  Comments ({comments.length})
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  data-testid="tab-history"
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'history'
                      ? 'border-primary text-stone-900'
                      : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <History className="w-3.5 h-3.5" strokeWidth={2} />
                  Task History ({history.length})
                </button>
              </div>

              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <>
                  <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                    {comments.length === 0 && (
                      <p className="text-sm text-stone-400 py-4 text-center">No comments yet</p>
                    )}
                    {comments.map((comment) => (
                  <div
                    key={comment.comment_id}
                    className="bg-stone-50 rounded-lg p-3 border border-stone-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-stone-800">
                        {comment.user_name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                        {comment.user_id === currentUserId && (
                          <button
                            onClick={() => handleDeleteComment(comment.comment_id)}
                            className="p-1 hover:bg-rose-100 rounded transition-colors"
                            title="Delete comment"
                            data-testid={`delete-comment-${comment.comment_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div 
                      className="text-sm text-stone-600 whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(renderCommentWithMentions(comment.content), { ADD_ATTR: ['target', 'rel'] })
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Add Comment with Mentions */}
              <div className="space-y-2">
                <MentionTextarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  employees={users}
                  onMentionsChange={setCommentMentions}
                  placeholder="Add a comment... (type @ to mention someone)"
                  data-testid="add-comment-input"
                  className="w-full"
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    data-testid="submit-comment-button"
                    className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold"
                  >
                    Add Comment
                  </Button>
                </div>
              </div>
                </>
              )}

              {/* Task History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-0 max-h-[380px] overflow-y-auto" data-testid="task-history-list">
                  {history.length === 0 ? (
                    <p className="text-sm text-stone-400 py-8 text-center">No movement history yet</p>
                  ) : (
                    history.map((h, i) => (
                      <div key={h.history_id || `hist-${i}`} className="flex gap-3 py-3 border-b border-stone-100 last:border-b-0" data-testid={`history-entry-${i}`}>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <ArrowRight className="w-4 h-4 text-primary" strokeWidth={2} />
                        </div>
                        <div>
                          <p className="text-sm text-stone-800">
                            Moved from <span className="font-semibold text-stone-900">'{h.from_column}'</span> to <span className="font-semibold text-stone-900">'{h.to_column}'</span>
                          </p>
                          <p className="text-xs text-stone-500 mt-0.5">
                            {new Date(h.moved_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} by {h.moved_by_name}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <h4 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                Attachments ({attachments.length})
              </h4>

              {/* Upload Area */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-stone-300 hover:border-primary hover:bg-stone-50'
                }`}
                data-testid="file-upload-area"
              >
                <input {...getInputProps()} />
                <Paperclip className="w-8 h-8 text-stone-400 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-stone-600">
                  {isDragActive
                    ? 'Drop the files here...'
                    : 'Drag & drop files here, or click to select'}
                </p>
                <p className="text-xs text-stone-500 mt-1">Images, PDFs, Videos (max 50MB for videos, 10MB for others)</p>
              </div>

              {/* Attachments List */}
              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.attachment_id}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Paperclip className="w-4 h-4 text-stone-400 flex-shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-800 truncate">
                            {attachment.file_name}
                          </p>
                          <p className="text-xs text-stone-500">
                            {attachment.uploaded_by_name} •{' '}
                            {new Date(attachment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => downloadAttachment(attachment)}
                          className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
                          data-testid={`download-attachment-${attachment.attachment_id}`}
                          title="Download"
                        >
                          <Download className="w-4 h-4 text-stone-600" strokeWidth={1.5} />
                        </button>
                        {attachment.uploaded_by === currentUserId && (
                          <button
                            onClick={() => handleDeleteAttachment(attachment.attachment_id)}
                            className="p-2 hover:bg-rose-100 rounded-lg transition-colors"
                            data-testid={`delete-attachment-${attachment.attachment_id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Priority */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Priority
              </label>
              {editMode ? (
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="edit-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${
                    ticket.priority === 'high'
                      ? 'bg-rose-50 text-rose-700 border-rose-200'
                      : ticket.priority === 'medium'
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'bg-stone-100 text-stone-600 border-stone-200'
                  }`}
                >
                  {ticket.priority === 'high' && (
                    <AlertCircle className="w-4 h-4" strokeWidth={2} />
                  )}
                  {ticket.priority}
                </span>
              )}
            </div>

            {/* Status/Column */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Status
              </label>
              {editMode ? (
                <Select
                  value={formData.column_id}
                  onValueChange={(value) => setFormData({ ...formData, column_id: value })}
                >
                  <SelectTrigger data-testid="edit-column-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((column) => (
                      <SelectItem key={column.column_id} value={column.column_id}>
                        {column.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-stone-800">
                  {columns.find((c) => c.column_id === ticket.column_id)?.name || 'Unknown'}
                </p>
              )}
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <User className="w-3 h-3" strokeWidth={1.5} />
                Assignee
              </label>
              {editMode ? (
                <Select
                  value={formData.assignee_user_id}
                  onValueChange={(value) => setFormData({ ...formData, assignee_user_id: value })}
                >
                  <SelectTrigger data-testid="edit-assignee-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2 sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-stone-400" />
                        <input
                          className="w-full rounded-md border border-stone-200 bg-stone-50 py-2 pl-8 pr-3 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder="Search by name or email..."
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          data-testid="edit-assignee-search-input"
                        />
                      </div>
                    </div>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {filteredUsers.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name}
                      </SelectItem>
                    ))}
                    {filteredUsers.length === 0 && assigneeSearch && (
                      <div className="px-3 py-2 text-xs text-stone-400">No users found</div>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-stone-800">{ticket.assignee_name || 'Unassigned'}</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" strokeWidth={1.5} />
                Due Date
              </label>
              {editMode ? (
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="edit-due-date"
                />
              ) : (
                <p className="text-sm text-stone-800">
                  {ticket.due_date
                    ? new Date(ticket.due_date + 'T12:00:00').toLocaleDateString()
                    : 'No due date'}
                </p>
              )}
            </div>

            {/* Time To Complete */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Timer className="w-3 h-3" strokeWidth={1.5} />
                Time To Complete
              </label>
              {ticket.time_to_complete ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium">
                  <Timer className="w-4 h-4" strokeWidth={1.5} />
                  {formatTimeToComplete(ticket.time_to_complete)}
                </span>
              ) : (
                <p className="text-sm text-stone-500">Not calculated yet</p>
              )}
            </div>

            {/* Created */}
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Created
              </label>
              <p className="text-sm text-stone-800">
                {new Date(ticket.created_at).toLocaleString()}
              </p>
              <p className="text-xs text-stone-500 mt-1">by {ticket.created_by_name}</p>
            </div>

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <div className="space-y-4 pt-4 border-t border-stone-200">
                <h4 className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Custom Fields
                </h4>
                {customFields.map((field) => {
                  const selectedValues = (editMode ? formData.custom_fields : ticket.custom_fields)?.[field.field_id] || [];
                  return (
                    <div key={field.field_id}>
                      <label className="block text-xs font-medium text-stone-600 mb-2">
                        {field.name}
                        {field.is_mandatory && <span className="text-rose-500 ml-1">*</span>}
                      </label>
                      {editMode ? (
                        <div className="flex flex-wrap gap-2">
                          {field.options.map((option) => (
                            <label
                              key={option.value}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer transition-colors text-xs ${
                                selectedValues.includes(option.value)
                                  ? 'bg-primary/10 border-primary text-primary'
                                  : 'bg-white border-stone-200 hover:bg-stone-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedValues.includes(option.value)}
                                onChange={(e) => handleCustomFieldChange(field.field_id, option.value, e.target.checked)}
                                className="sr-only"
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {selectedValues.length > 0 ? (
                            selectedValues.map((val) => {
                              const option = field.options.find(o => o.value === val);
                              return (
                                <span key={val} className="inline-flex px-2 py-1 bg-stone-100 text-stone-700 rounded-lg text-xs">
                                  {option?.label || val}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-xs text-stone-400">Not set</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketDetailModal;
